import React, { useMemo, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Badge,
  Button,
  Icon,
  Collapse,
  Divider,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { FiZap, FiInbox, FiPlus, FiChevronDown, FiChevronUp, FiRadio } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import { useContactCampaigns } from '../../../hooks/useContactCampaigns';
import { useUserCampaigns } from '../../../hooks/useUserCampaigns';
import { useCrmUsers } from '../../../hooks/useCrmUsers';
import { getTriggerDescription } from './campaignConstants';
import CampaignEnrollmentCard from './CampaignEnrollmentCard';
import EnrollCampaignModal from './EnrollCampaignModal';

function PassiveCampaignsSection({ userCampaigns, enrolledCampaignIds }) {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: false });
  const dimmedText = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const rowBg = useColorModeValue('gray.50', 'gray.800');

  const passiveCampaigns = useMemo(() => {
    return userCampaigns.filter(
      (c) => c.isEnabled && !enrolledCampaignIds.has(c.id)
    );
  }, [userCampaigns, enrolledCampaignIds]);

  if (!passiveCampaigns.length) return null;

  return (
    <Box mt={2}>
      <Divider mb={3} />
      <HStack
        cursor="pointer"
        onClick={onToggle}
        spacing={2}
        py={1}
        _hover={{ opacity: 0.8 }}
      >
        <Icon as={FiRadio} color={dimmedText} boxSize="13px" />
        <Text fontSize="xs" fontWeight="600" color={dimmedText}>
          Passive Campaigns ({passiveCampaigns.length})
        </Text>
        <Icon
          as={isOpen ? FiChevronUp : FiChevronDown}
          color={dimmedText}
          boxSize="14px"
        />
      </HStack>
      <Collapse in={isOpen} animateOpacity>
        <VStack align="stretch" spacing={2} mt={2}>
          {passiveCampaigns.map((c) => {
            const template = c.template || {};
            const triggerDesc = getTriggerDescription(
              template.triggerType,
              template.triggerConfig
            );
            const allowReEnroll = template.enrollmentRules?.allow_re_enrollment;

            return (
              <Box
                key={c.id}
                p={2.5}
                bg={rowBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="md"
                opacity={0.75}
              >
                <HStack justify="space-between" mb={0.5}>
                  <HStack spacing={2} minW={0}>
                    <Icon as={FiZap} color={dimmedText} boxSize="12px" flexShrink={0} />
                    <Text fontSize="xs" fontWeight="600" color={dimmedText} noOfLines={1}>
                      {template.name || 'Unnamed Campaign'}
                    </Text>
                  </HStack>
                  <HStack spacing={1} flexShrink={0}>
                    <Badge colorScheme="purple" fontSize="2xs" variant="outline">
                      {(template.triggerType || '').replace(/_/g, ' ')}
                    </Badge>
                    {allowReEnroll && (
                      <Badge colorScheme="orange" fontSize="2xs" variant="subtle">Auto</Badge>
                    )}
                  </HStack>
                </HStack>
                <Text fontSize="2xs" color={dimmedText} pl="20px">
                  {triggerDesc}
                </Text>
              </Box>
            );
          })}
        </VStack>
      </Collapse>
    </Box>
  );
}

export default function ContactCampaignPanel({ contactId, onActiveCountChange }) {
  const { currentUser } = useAuth();
  const { enrollments, stats, loading, error, enrollContact, unenrollContact } =
    useContactCampaigns(contactId);
  const { campaigns: userCampaigns } = useUserCampaigns(currentUser?.uid);
  const { getUserName } = useCrmUsers();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const emptyBg = useColorModeValue('gray.50', 'gray.800');

  // Enrollments can belong to another user's campaign (e.g. an admin viewing
  // a deal enrolled in an LO's personal campaign) — attribute those.
  const ownerNameFor = (enrollment) => {
    const ownerId = enrollment.userCampaign?.userId;
    if (!ownerId || ownerId === currentUser?.uid) return null;
    return getUserName(ownerId);
  };

  const enrolledCampaignIds = useMemo(
    () => new Set(enrollments.map((e) => e.userCampaign.id)),
    [enrollments]
  );

  // Lets a parent (e.g. the DealPage Campaigns tab badge) mirror the number of
  // campaigns this contact is actively enrolled in, without a duplicate fetch.
  useEffect(() => {
    onActiveCountChange?.(stats.active);
  }, [stats.active, onActiveCountChange]);

  if (loading) {
    return (
      <Center py={12}>
        <VStack spacing={3}>
          <Spinner size="lg" color="blue.400" />
          <Text fontSize="sm" color="gray.500">Loading campaigns...</Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">{error}</Text>
      </Alert>
    );
  }

  if (!enrollments.length) {
    return (
      <>
        <Center py={12}>
          <VStack spacing={3}>
            <Box p={4} borderRadius="full" bg={emptyBg}>
              <Icon as={FiInbox} boxSize="28px" color="gray.400" />
            </Box>
            <Text fontSize="sm" color="gray.500" fontWeight="500">
              Not enrolled in any campaigns
            </Text>
            <Text fontSize="xs" color="gray.400" textAlign="center" maxW="260px">
              Enroll this contact in an automation campaign to start tracking their progress.
            </Text>
            <Button size="sm" colorScheme="blue" leftIcon={<FiPlus />} onClick={onOpen} mt={1}>
              Enroll in Campaign
            </Button>
          </VStack>
        </Center>

        <PassiveCampaignsSection
          userCampaigns={userCampaigns}
          enrolledCampaignIds={enrolledCampaignIds}
        />

        <EnrollCampaignModal
          isOpen={isOpen}
          onClose={onClose}
          enrolledCampaignIds={enrolledCampaignIds}
          onEnroll={enrollContact}
        />
      </>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      {/* Summary stats + enroll button */}
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <HStack spacing={3} flexWrap="wrap">
          <HStack spacing={1}>
            <Icon as={FiZap} color="blue.400" boxSize="14px" />
            <Text fontSize="xs" fontWeight="600" color="gray.600" _dark={{ color: 'gray.300' }}>
              {stats.total} Campaign{stats.total !== 1 ? 's' : ''}
            </Text>
          </HStack>
          {stats.active > 0 && (
            <Badge colorScheme="blue" fontSize="2xs" variant="subtle">
              {stats.active} active
            </Badge>
          )}
          {stats.completed > 0 && (
            <Badge colorScheme="green" fontSize="2xs" variant="subtle">
              {stats.completed} completed
            </Badge>
          )}
          {stats.failed > 0 && (
            <Badge colorScheme="red" fontSize="2xs" variant="subtle">
              {stats.failed} failed
            </Badge>
          )}
        </HStack>
        <Button size="xs" colorScheme="blue" variant="outline" leftIcon={<FiPlus />} onClick={onOpen}>
          Enroll
        </Button>
      </HStack>

      {/* Enrollment cards */}
      {enrollments.map((enrollment) => (
        <CampaignEnrollmentCard
          key={enrollment.userCampaign.id}
          enrollment={enrollment}
          onRemove={unenrollContact}
          ownerName={ownerNameFor(enrollment)}
        />
      ))}

      {/* Passive campaigns — collapsed by default */}
      <PassiveCampaignsSection
        userCampaigns={userCampaigns}
        enrolledCampaignIds={enrolledCampaignIds}
      />

      <EnrollCampaignModal
        isOpen={isOpen}
        onClose={onClose}
        enrolledCampaignIds={enrolledCampaignIds}
        onEnroll={enrollContact}
      />
    </VStack>
  );
}
